import Modal from "@common/Modal";
import { useEffect, useState } from "react";

export type FeatureIncludedItem = {
    title: string;
    amount?: string;
    description?: string;
    defaultExpanded?: boolean;
};

export type FeatureIncludedGroup = {
    title: string;
    items: FeatureIncludedItem[];
};

type FeatureIncludedPopupProps = {
    isOpen: boolean;
    onClose: () => void;
    title: string;
    description?: string;
    features: FeatureIncludedGroup[];
    primaryActionText?: string;
    onPrimaryAction?: () => void;
    className?: string;
};

export default function FeatureIncludedPopup({
    isOpen,
    onClose,
    title,
    description,
    features,
    primaryActionText,
    onPrimaryAction,
    className = "",
}: FeatureIncludedPopupProps) {
    const defaultExpandedKey = features
        .flatMap((group, groupIndex) => group.items.map((item, itemIndex) => ({ item, key: `${groupIndex}-${itemIndex}` })))
        .find(({ item }) => item.defaultExpanded && item.description)?.key || "";
    const [expandedKey, setExpandedKey] = useState(defaultExpandedKey);

    useEffect(() => {
        if (!isOpen) {
            setExpandedKey(defaultExpandedKey);
            return;
        }

        setExpandedKey(defaultExpandedKey);
        document.body.classList.add("overflow-hidden");

        const handleEscape = (event: KeyboardEvent) => {
            if (event.key === "Escape") onClose();
        };

        document.addEventListener("keydown", handleEscape);

        return () => {
            document.body.classList.remove("overflow-hidden");
            document.removeEventListener("keydown", handleEscape);
        };
    }, [defaultExpandedKey, isOpen, onClose]);

    const toggleFeature = (key: string, hasDescription: boolean) => {
        if (!hasDescription) return;
        setExpandedKey((currentKey) => currentKey === key ? "" : key);
    };

    return (
        <div className={`model-outer travel-feature-included-popup${className ? ` ${className}` : ""}`}>
            <Modal show={isOpen} handleClose={onClose}>
                <div className="travel-feature-popup">
                    <div className="travel-feature-popup-head">
                        <h2 className="heading-h2">{title}</h2>
                        {description ? <p className="f-13">{description}</p> : null}
                    </div>

                    <div className="travel-feature-popup-body">
                        {features.map((group, groupIndex) => (
                            <section className="travel-feature-group" key={`${group.title}-${groupIndex}`}>
                                <h3 className="f-20">{group.title}</h3>

                                <div className="travel-feature-list">
                                    {group.items.map((item, itemIndex) => {
                                        const itemKey = `${groupIndex}-${itemIndex}`;
                                        const isExpanded = expandedKey === itemKey;
                                        const hasDescription = Boolean(item.description);
                                        const featureRowContent = (
                                            <>
                                                <span className="travel-feature-name f-13">{item.title}</span>
                                                {item.amount ? <span className="travel-feature-amount f-13">{item.amount}</span> : null}
                                            </>
                                        );

                                        return (
                                            <div className="travel-feature-item" key={`${item.title}-${itemIndex}`}>
                                                {hasDescription ? (
                                                    <button
                                                        type="button"
                                                        className="travel-feature-row travel-feature-row-action"
                                                        onClick={() => toggleFeature(itemKey, hasDescription)}
                                                        aria-expanded={isExpanded}
                                                    >
                                                        {featureRowContent}
                                                    </button>
                                                ) : (
                                                    <div className="travel-feature-row">
                                                        {featureRowContent}
                                                    </div>
                                                )}

                                                {hasDescription && isExpanded ? (
                                                    <div className="travel-feature-description f-13">
                                                        {item.description}
                                                    </div>
                                                ) : null}
                                            </div>
                                        );
                                    })}
                                </div>
                            </section>
                        ))}
                    </div>

                    {primaryActionText ? (
                        <div className="travel-feature-popup-footer">
                            <button type="button" className="btn-primary" onClick={onPrimaryAction}>
                                {primaryActionText}
                            </button>
                        </div>
                    ) : null}
                </div>
            </Modal>
        </div>
    );
}
